//LIBRARIES
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { CSSTransitionGroup } from 'react-transition-group';
import { CardText } from 'material-ui/Card';
import Card from '@material-ui/core/Card';
import RaisedButton from 'material-ui/RaisedButton';
import Lottie from 'react-lottie';
import moment from 'moment';
import styled from 'styled-components';
import { Typography } from 'antd';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { differenceIn } from 'helpers/Formatter';
import { featureEnabled } from 'helpers/ToggleBot';
import { getCurrentLocale } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

//COMPONENTS
import { constants } from 'components/byesign/ByesignHelper';
import FooterButtons from 'components/shared/FooterButtons';
import { ApplicationStatus } from 'components/shared/StatusHelper';
import ApplicationOptions from '../ApplicationOptions';
import CtaPrimary from 'components/ui/CtaPrimary';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import CardWithIconHeader from 'components/ui/CardWithIconHeader';
import Spacer from 'components/ui/Spacer';
import ApplicantDialog from './ApplicantDialog';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import ApplicantsDetails from './ApplicantsDetails';
import AddDocumentDialog from './AddDocumentDialog';
import Header from 'components/shared/Header';
import { formatMoney, toFriendlyDateString, toFriendlyDateTimeString } from 'helpers/Formatter';
import { RenderLeaseItem } from 'components/shared/LeaseItems/helpers';
import ManualApplicantDrawer from 'components/landlord_app/applications/ManualApplicantDrawer/index';
import SearchableSelect from 'components/shared/SearchableSelect';
import CreditCheckOptions from './CreditCheckOptions';

//ACTIONS
import { getUnits } from 'actions/landlordPropertiesActions';
import { getListOfMaintenanceStaff } from 'actions/maintenanceActions';
import { createOrUpdateApplicant, updateApplication } from 'actions/landlordApplicationsActions';
import { openEditLease } from 'actions/LeaseDetailsActions';
import { sendInvitationOrPassReset, bypassSignature } from 'actions/landlordTenantsActions';
import { uploadLeadDocuments } from 'actions/landlordLeadsActions';
import { getLeadByEmail } from 'actions/landlordLeadsActions';
import { openDocument } from 'actions/landlordAPIDashboardActions';

//ASSETS
import eventIcon from 'assets/CheckCal_.svg';
import noteIcon from 'assets/note_png.png';
import cautionAnimationData from 'assets/caution.json';
import { browserHistory } from 'react-router';

const { Title } = Typography;

const StyledIcon = styled.img`
  width: 25px;
  display: 'inline-flex';
`;

const StyledSecondaryButton = styled.button`
  margin: 10px;
`;

const defaultOptions = {
  loop: false,
  autoplay: true,
  animationData: cautionAnimationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const pageViewHeaders = {
  DETAILS: { name: 'details', formName: 'lease_details' },
  APPLICANTS: { name: 'applicants', formName: 'applicants' },
  OPTIONS: { name: 'options', formName: '' },
  EVENTS: { name: 'events', formName: '' },
};

let style = {
  transaction_card: {
    color: '#424242 !important',
    fontFamily: '"Circular", sans-serif !important',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  drawerHeader: {
    width: '100%',
    height: '65px',
    padding: '10px',
    backgroundColor: 'white',
  },
};

export default connect(
  store => ({
    application: store.landlordApplications.application,
    yardiEnabled: store.userTypes.yardiEnabled,
    propertyAgents: store.maintenanceReducer.maintenance,
  }),
  {
    getListOfMaintenanceStaff,
    updateApplication,
    bypassSignature,
    openEditLease,
    sendInvitationOrPassReset,
    createOrUpdateApplicant,
    uploadLeadDocuments,
    getLeadByEmail,
    openDocument,
    getUnits,
  }
)(
  class ApplicationOverview extends Component {
    constructor(props) {
      super(props);
      const params = browserHistory.getCurrentLocation().query;
      let defaultPage = pageViewHeaders.APPLICANTS.name;

      if (params.drawer_page) {
        defaultPage = params.drawer_page;
      }

      this.state = {
        openView: defaultPage,
        confirmationDialogOpen: false,
        applicantDialogOpen: false,
        toggleBypassSignatureConfirmation: false,
        isBypassSignatureButtonLoading: false,
        addDocumentDialogOpen: false,
        manualApplicantEntryDrawerOpen: false,
        applicant: null,
        selectedAgentID: null,
        isSyncing: false,
        disableSendBtn: false,
      };
    }

    componentDidMount() {
      this.props.getListOfMaintenanceStaff(this.props.application.property_id, {
        agents_only: true,
      });

      if (
        this.props.application.delay_unit_assignment &&
        this.props.application.status === 'pending_unit_assignment'
      ) {
        this.getAvailableUnits(this.props.application.property_id);
      }
    }

    getAvailableUnits = propertyId => {
      this.props.getUnits(propertyId, {
        for_multiselect: true,
        only_rentables: this.props.yardiEnabled,
      });
    };

    syncWithYardi = applicationId => {
      this.setState({ isSyncing: true });
      this.props.syncApplicationWithYardi(applicationId);
    };

    cancelApplication = applicationId => {
      this.setState({ isCanceling: true });
      this.props.cancelApplication(applicationId).then(() => {
        this.setState({ isCanceling: false });
      });
    };

    pageView = pageToView => {
      this.setState({
        openView: pageToView.name,
      });
    };

    toggleManualApplicantEntry = (applicant = null) => {
      this.setState({
        manualApplicantEntryDrawerOpen: !this.state.manualApplicantEntryDrawerOpen,
        applicant: applicant
          ? {
              ...applicant,
              // Converting string into a JS date Object using moment because Datepicker UI component only accepts that for initialValue
              // Check if there is a dob stored for an applicant otherwise we set the initialValue to empty date
              dob: applicant.dob ? moment(applicant.dob).toDate() : '',
            }
          : null,
      });
    };

    toggleApplicantDialog = (applicant = null) => {
      this.setState({
        applicantDialogOpen: !this.state.applicantDialogOpen,
        applicant: applicant
          ? {
              ...applicant,
              // Converting string into a JS date Object using moment because Datepicker UI component only accepts that for initialValue
              // Check if there is a dob stored for an applicant otherwise we set the initialValue to empty date
              dob: applicant.dob ? moment(applicant.dob).toDate() : '',
            }
          : null,
      });
    };

    toggleAddDocumentDialog = (applicant = null) => {
      this.setState({
        addDocumentDialogOpen: !this.state.addDocumentDialogOpen,
        applicant,
      });
    };

    addDocument = async formData => {
      this.props.uploadLeadDocuments(formData, this.state.applicant.id);
      this.toggleAddDocumentDialog();
    };

    toggleConfirmationDialog = (applicant = null) => {
      this.setState({
        confirmationDialogOpen: !this.state.confirmationDialogOpen,
        applicant,
      });
    };

    toggleBypassSignature = (applicant = null) => {
      this.setState({
        toggleBypassSignatureConfirmation: !this.state.toggleBypassSignatureConfirmation,
        isBypassSignatureButtonLoading: false,
        applicant,
      });
    };

    onByPassSignature = async () => {
      const { getApplication, openEditLease, application, bypassSignature } = this.props;

      this.setState({ isBypassSignatureButtonLoading: true });

      //Update the backend
      await bypassSignature(this.state.applicant.id);

      //Reload application and event tab data
      await getApplication(application.id);
      await openEditLease(application.lease_id);

      this.setState({
        toggleBypassSignatureConfirmation: false,
      });
    };

    toggleInvitationOrPassResetDialog = (applicant = null) => {
      this.setState({
        showInvitationOrPassResetDialog: !this.state.showInvitationOrPassResetDialog,
        applicant,
      });
    };

    sendInvitationOrPassReset = async () => {
      this.setState({ showInvitationOrPassResetDialog: false, disableSendBtn: true });
      await this.props.sendInvitationOrPassReset(this.state.applicant.id);
      this.setState({ disableSendBtn: false });
    };

    deleteApplicant = async () => {
      const response = await this.props.deleteApplicant(
        this.props.application.id,
        this.state.applicant.id
      );
      if (response) {
        await this.props.getApplication(this.props.application.id);
        this.toggleConfirmationDialog();
      }
    };

    onAgentSelect = selectedAgent => {
      if (!selectedAgent) {
        return;
      }

      this.setState({ selectedAgentID: selectedAgent.value });
    };

    reassignAgent = () => {
      this.props.updateApplication(this.props.application.id, {
        reassign_to: this.state.selectedAgentID,
      });
    };

    renderEventTitle = event => {
      let titleIcon = eventIcon;
      if (event.event_type === 'note') {
        titleIcon = noteIcon;
      }

      return (
        <React.Fragment>
          <StyledIcon src={titleIcon} alt="loader" />
          <div style={{ display: 'inline-grid', marginLeft: '16px', verticalAlign: 'top' }}>
            <Title level={4} style={{ margin: 0, textTransform: 'capitalize' }}>
              {event.event_type}
            </Title>
            <p style={{ margin: 0, color: colors.FADE_GRAY, fontSize: '10px' }}>
              {toFriendlyDateTimeString(event.time, { language: getCurrentLocale() })}
            </p>
          </div>
        </React.Fragment>
      );
    };

    render() {
      const { handleSubmit, toggleDrawer, leaseDetails } = this.props;
      const columnSize = leaseDetails.lease_start_date ? 3 : 4;
      const applicationHeader = leaseDetails.unit
        ? `Unit ${leaseDetails.unit}`
        : 'Application Details';
      const leaseItems =
        columnSize === 3 && leaseDetails.lease_items.length > 0 ? leaseDetails.lease_items : [];

      const is_moving_in_early =
        differenceIn(leaseDetails.early_move_in_date, leaseDetails.lease_start_date, 'days') === 0;

      const preventUnsyncedApplicationActions =
        featureEnabled('ui.applications.prevent_application_actions_if_unsynced') &&
        !this.props.application.lease_external_id &&
        !this.props.application.delay_unit_assignment;

      const completedApplication = this.props.application.status === 'payment_received';
      const canceledApplication = this.props.application.status === 'cancelled';

      let deleteDialogBodyVerbiage = getTranslatedString(
        'landlordPortal.applicantDialog.confirmation.deleteApplicantBody'
      );
      let hideConfirmation = false;
      if (
        this.state.applicant &&
        this.state.applicant.is_primary_applicant &&
        this.props.yardiEnabled
      ) {
        deleteDialogBodyVerbiage = getTranslatedString(
          'landlordPortal.applicantDialog.confirmation.cannotDeletePrimaryApplicantBody'
        );
        hideConfirmation = true;
      }

      let deleteDialogTitleVerbiage = getTranslatedString(
        'landlordPortal.applicantDialog.confirmation.deleteApplicantTitle'
      );
      if (
        this.state.applicant &&
        this.state.applicant.is_primary_applicant &&
        this.props.yardiEnabled
      ) {
        deleteDialogTitleVerbiage = getTranslatedString(
          'landlordPortal.applicantDialog.confirmation.cannotDeletePrimaryApplicantTitle'
        );
      }

      return (
        <Fragment>
          <div className="row" style={style.drawerHeader}>
            <h2 style={{ textAlign: 'center', margin: '8px 0' }}>
              <div className="row">
                <p
                  style={{
                    maxWidth: '225px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    display: '-webkit-inline-box',
                  }}
                >
                  {applicationHeader}
                </p>
                {
                  <ApplicationStatus
                    style={{
                      fontSize: '12px',
                      marginLeft: '5px',
                      padding: '5px',
                    }}
                    status={this.props.application.status}
                  />
                }
              </div>
            </h2>
          </div>
          {!preventUnsyncedApplicationActions && (
            <div
              className="row"
              style={{ width: '100%', height: '29px', padding: '0 10px', backgroundColor: 'white' }}
            >
              <div className={`col-xs-${columnSize}`}>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => this.pageView(pageViewHeaders.APPLICANTS)}
                    style={{
                      color: AccentColor(),
                      fontFamily: 'Circular-Bold',
                      borderBottom:
                        this.state.openView === pageViewHeaders.APPLICANTS.name
                          ? '2px solid'
                          : 'none',
                      borderRadius: '0px !important',
                    }}
                  >
                    Applicants
                  </button>
                </div>
              </div>
              {columnSize === 3 && (
                <div className={`col-xs-${columnSize}`}>
                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => this.pageView(pageViewHeaders.DETAILS)}
                      style={{
                        color: AccentColor(),
                        fontFamily: 'Circular-Bold',
                        borderBottom:
                          this.state.openView === pageViewHeaders.DETAILS.name
                            ? '2px solid'
                            : 'none',
                        borderRadius: '0px !important',
                      }}
                    >
                      Details
                    </button>
                  </div>
                </div>
              )}
              <div className={`col-xs-${columnSize}`}>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => this.pageView(pageViewHeaders.OPTIONS)}
                    style={{
                      color: AccentColor(),
                      fontFamily: 'Circular-Bold',
                      borderBottom:
                        this.state.openView === pageViewHeaders.OPTIONS.name ? '2px solid' : 'none',
                      borderRadius: '0px !important',
                    }}
                  >
                    Options
                  </button>
                </div>
              </div>
              <div className={`col-xs-${columnSize}`}>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => this.pageView(pageViewHeaders.EVENTS)}
                    style={{
                      color: AccentColor(),
                      fontFamily: 'Circular-Bold',
                      borderBottom:
                        this.state.openView === pageViewHeaders.EVENTS.name ? '2px solid' : 'none',
                      borderRadius: '0px !important',
                    }}
                  >
                    Events
                  </button>
                </div>
              </div>
            </div>
          )}
          <div
            onSubmit={handleSubmit}
            style={{
              padding: '15px',
              height: 'calc(100% - 155px)',
              overflow: 'scroll',
              backgroundColor: '#fbfbfb',
            }}
          >
            <CSSTransitionGroup
              transitionName="fade_out_buttons"
              transitionEnterTimeout={200}
              transitionLeaveTimeout={200}
            >
              {preventUnsyncedApplicationActions &&
                this.props.application.unit_is_rentable && (
                  <DrawerSuccessMessages
                    headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
                    headerText="This application has not been synced with Yardi yet"
                    bodyText="This is likely because you are actioning an older application. Click below to sync this application with Yardi to resume the leasing process."
                    cta={
                      !canceledApplication && (
                        <CtaPrimary
                          disabled={this.state.isSyncing}
                          onClick={() => this.syncWithYardi(this.props.application.id)}
                        >
                          Sync Application
                        </CtaPrimary>
                      )
                    }
                    secondaryCta={
                      !completedApplication &&
                      !canceledApplication && (
                        <StyledSecondaryButton
                          className="link-button"
                          disabled={this.state.isCanceling}
                          onClick={() => this.cancelApplication(this.props.application.id)}
                        >
                          Cancel Application
                        </StyledSecondaryButton>
                      )
                    }
                    thirdCta={
                      <StyledSecondaryButton
                        className="link-button"
                        onClick={this.props.openLeaseCreator}
                      >
                        View Applicant Details
                      </StyledSecondaryButton>
                    }
                    fourthCta={
                      leaseDetails.document_uuid && (
                        <StyledSecondaryButton
                          className="link-button"
                          onClick={() =>
                            this.props.openDocument(
                              constants.PRODUCTION,
                              leaseDetails.document_uuid
                            )}
                        >
                          View Most Recent Lease
                        </StyledSecondaryButton>
                      )
                    }
                  />
                )}
              {preventUnsyncedApplicationActions &&
                !this.props.application.unit_is_rentable && (
                  <DrawerSuccessMessages
                    headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
                    headerText="This application has not been synced with Yardi yet"
                    bodyText="This is likely because you are actioning an older application. The unit this application is set for is no longer available. Make the unit available on Yardi, or generate a new application for an available unit."
                    cta={
                      !completedApplication &&
                      !canceledApplication && (
                        <CtaPrimary
                          disabled={this.state.isCanceling}
                          onClick={() => this.cancelApplication(this.props.application.id)}
                        >
                          Cancel Application
                        </CtaPrimary>
                      )
                    }
                    secondaryCta={
                      <StyledSecondaryButton
                        className="link-button"
                        onClick={this.props.openLeaseCreator}
                      >
                        View Applicant Details
                      </StyledSecondaryButton>
                    }
                    thirdCta={
                      leaseDetails.document_uuid && (
                        <StyledSecondaryButton
                          className="link-button"
                          onClick={() =>
                            this.props.openDocument(
                              constants.PRODUCTION,
                              leaseDetails.document_uuid
                            )}
                        >
                          View Most Recent Lease
                        </StyledSecondaryButton>
                      )
                    }
                  />
                )}
              {this.state.openView === pageViewHeaders.DETAILS.name &&
                !preventUnsyncedApplicationActions && (
                  <div className="row">
                    {/************ Lease Details **************/}
                    <h3 style={{ margin: '0 0 15px' }}>Lease Overview</h3>

                    <Card style={style.transaction_card}>
                      <CardText expandable={true} style={{ padding: '0 0 10px' }}>
                        <div className="row">
                          <Header name="LEASE DETAILS" />
                          <div className="row" style={{ margin: '20px 0 20px' }}>
                            {leaseDetails.early_move_in_date &&
                              this._renderEarlyMoveInDate(leaseDetails)}
                            <div className="col-xs-6">
                              <h5 style={{ margin: '0', textAlign: 'center' }}>
                                {' '}
                                Lease Start Date{' '}
                              </h5>
                              <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                {toFriendlyDateString(leaseDetails.lease_start_date)}
                              </h2>
                            </div>
                            <div className="col-xs-6">
                              <h5 style={{ margin: '0', textAlign: 'center' }}> Lease End Date </h5>
                              <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                {toFriendlyDateString(leaseDetails.lease_end_date)}
                              </h2>
                            </div>
                            {is_moving_in_early && (
                              <div className="col-xs-12">
                                <h5
                                  style={{
                                    marginBottom: '0',
                                    marginTop: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  {' '}
                                  Early Move In Date{' '}
                                </h5>
                                <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                  {toFriendlyDateString(leaseDetails.early_move_in_date)}
                                </h2>
                              </div>
                            )}
                          </div>
                          <Header name="LEASE PRICING" />
                          <div className="row" style={{ margin: '20px 0 20px' }}>
                            <div className="col-xs-6">
                              <h5 style={{ margin: '0', textAlign: 'center' }}> Monthly Rent </h5>
                              <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                {formatMoney(leaseDetails.monthly_rent)}
                              </h2>
                            </div>
                            <div className="col-xs-6">
                              <h5 style={{ margin: '0', textAlign: 'center' }}> Deposit </h5>
                              <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                {formatMoney(leaseDetails.deposit_amount)}
                              </h2>
                            </div>
                            {leaseDetails.key_deposit !== 0 && (
                              <div className="col-xs-6 col-xs-offset-3">
                                <h5 style={{ margin: '0', textAlign: 'center' }}> Key Deposit </h5>
                                <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
                                  {formatMoney(leaseDetails.key_deposit)}
                                </h2>
                              </div>
                            )}
                          </div>
                          {leaseItems.length > 0 && (
                            <Fragment>
                              <Header name="ANCILLARY ITEMS" />
                              <div
                                className="row"
                                style={{ margin: '5px 0px 15px', padding: '0 35px' }}
                              >
                                {RenderLeaseItem({ items: leaseItems, disabled: true })}
                              </div>
                            </Fragment>
                          )}
                          <ReassignAgent
                            status={this.props.application.status}
                            propertyAgents={this.props.propertyAgents}
                            onAgentSelect={this.onAgentSelect}
                            reassignAgent={this.reassignAgent}
                          />
                          <CreditCheckOptions application={this.props.application} />
                        </div>
                      </CardText>
                    </Card>
                  </div>
                )}

              {this.state.openView === pageViewHeaders.APPLICANTS.name &&
                !preventUnsyncedApplicationActions && (
                  <ApplicantsDetails
                    application={this.props.application}
                    getApplicationsAndClearOffset={this.props.getApplicationsAndClearOffset}
                    openLeaseCreator={this.props.openLeaseCreator}
                    toggleManualApplicantEntry={this.toggleManualApplicantEntry}
                    toggleApplicantDialog={this.toggleApplicantDialog}
                    toggleAddDocumentDialog={this.toggleAddDocumentDialog}
                    toggleConfirmationDialog={this.toggleConfirmationDialog}
                    toggleInvitationOrPassResetDialog={this.toggleInvitationOrPassResetDialog}
                    toggleBypassSignature={this.toggleBypassSignature}
                    toggleDrawer={toggleDrawer}
                  />
                )}

              {this.state.openView === pageViewHeaders.OPTIONS.name &&
                !preventUnsyncedApplicationActions && (
                  <div className="row">
                    <h3 style={{ margin: '0 0 15px' }}> Options </h3>
                    <ApplicationOptions
                      leaseDetails={leaseDetails}
                      applicationDetails={this.props.application}
                      toggleApplication={this.props.toggleApplication}
                      getApplicationsAndClearOffset={this.props.getApplicationsAndClearOffset}
                    />
                  </div>
                )}

              {this.state.openView === pageViewHeaders.EVENTS.name &&
                !preventUnsyncedApplicationActions && (
                  <div className="row">
                    <h3 style={{ margin: '0 0 15px' }}> Events </h3>
                    {leaseDetails.events.sort((a, b) => (a.time > b.time ? -1 : 1)).map(event => (
                      <React.Fragment>
                        <CardWithIconHeader title={this.renderEventTitle(event)}>
                          <Title level={4} style={{ marginBottom: 0, marginTop: '16px' }}>
                            {event.title}
                          </Title>
                          <p style={{ marginBottom: 0 }}>{event.verbiage}</p>
                        </CardWithIconHeader>
                        <Spacer mb={2} />
                      </React.Fragment>
                    ))}
                  </div>
                )}

              <ManualApplicantDrawer
                open={this.state.manualApplicantEntryDrawerOpen}
                onClose={this.toggleManualApplicantEntry}
                applicant={this.state.applicant}
                getApplicationsAndClearOffset={this.props.getApplicationsAndClearOffset}
              />
              <ApplicantDialog
                application={this.props.application}
                applicant={this.state.applicant}
                applicantDialogOpen={this.state.applicantDialogOpen}
                toggleApplicantDialog={this.toggleApplicantDialog}
                callback={async () => {
                  await this.props.getApplication(this.props.application.id);
                  const leaseID = this.props.application.lease_id;
                  if (leaseID) {
                    await this.props.openEditLease(leaseID);
                  }
                }}
                createOrUpdateApplicant={this.props.createOrUpdateApplicant}
              />
              <AddDocumentDialog
                toggleAddDocumentDialog={this.toggleAddDocumentDialog}
                addDocumentDialogOpen={this.state.addDocumentDialogOpen}
                onSubmit={this.addDocument}
                applicant={this.state.applicant}
              />
              <ConfirmationDialog
                showDialog={this.state.confirmationDialogOpen}
                hideDialog={this.toggleConfirmationDialog}
                userConfirmVerbiage="Delete"
                userConfirmAction={this.deleteApplicant}
                dialogTitle={deleteDialogTitleVerbiage}
                dialogBodyVerbiage={deleteDialogBodyVerbiage}
                hiddenCTA={hideConfirmation}
              />
              <ConfirmationDialog
                showDialog={this.state.toggleBypassSignatureConfirmation}
                hideDialog={this.toggleBypassSignature}
                disabled={this.state.isBypassSignatureButtonLoading}
                userConfirmVerbiage="confirm"
                userConfirmAction={this.onByPassSignature}
                dialogTitle={getTranslatedString(
                  'landlordPortal.applicantDialog.confirmation.question'
                )}
                dialogBodyVerbiage={getTranslatedString(
                  'landlordPortal.applicationDialog.bypass.signatureInfo'
                )}
              />
              <ConfirmationDialog
                showDialog={this.state.showInvitationOrPassResetDialog}
                hideDialog={this.toggleInvitationOrPassResetDialog}
                userConfirmVerbiage="Send"
                disabled={this.state.disableSendBtn}
                userConfirmAction={this.sendInvitationOrPassReset}
                dialogTitle="Confirmation"
                dialogBodyVerbiage="This sends out an email to user. Do you want to proceed?"
              />
            </CSSTransitionGroup>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              margin: '8px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <FooterButtons
              backName="Close"
              hideSubmitButton
              formName={this.state.formName}
              backAction={toggleDrawer}
            />
          </div>
        </Fragment>
      );
    }

    _renderEarlyMoveInDate = leaseDetails => (
      <div className="row" style={{ marginBottom: '15px' }}>
        <h5 style={{ margin: '0', textAlign: 'center' }}> Early Move-in Date </h5>
        <h2 style={{ textAlign: 'center', marginTop: '15px' }}>
          {toFriendlyDateString(leaseDetails.early_move_in_date)}
        </h2>
      </div>
    );
  }
);

const ReassignAgent = ({ status, propertyAgents, onAgentSelect, reassignAgent }) => {
  if (
    status === 'payment_received' &&
    !featureEnabled('ui.applications_page.modify_lease_when_payment_received')
  ) {
    return null;
  }
  return (
    <div>
      <Header name="RE-ASSIGN AGENT" />
      <div className="row" style={{ margin: '20px 0 20px' }}>
        <div className="col-xs-12">
          <p>
            If you would like to re-assign this application to another agent, select one below and
            click save.
          </p>
          <SearchableSelect
            name="assigned_agent"
            data={propertyAgents}
            label="Assigned Agent"
            fullWidth
            onChange={onAgentSelect}
          />
          <div className="row" style={{ textAlign: 'center' }}>
            <RaisedButton
              label="Save"
              primary
              onClick={reassignAgent}
              style={{
                backgroundColor: AccentColor(),
                color: 'white',
                borderRadius: '8px',
                marginTop: '10px',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
